"use client";

import { useRef } from "react";
import { GlowCard, GradText, SectionHead, SectionShell, useBandRowAlign, WipTag } from "./kit";
import { useEnterStagger } from "./kit/motion";
import { roadmapItem } from "@/lib/roadmap";

/** Blue wash rgba(47,107,224,0.065) composited over PAGE_FILL #f8fafc. */
const WASH_FILL = "#ebf1fa";

/**
 * Beyond links — email forwarding (live) + nameserver delegation (roadmap)
 * as compact glow cards on the wash band (replaces the two full legacy
 * sections on this page; those files stay in the library untouched).
 */
export default function DomainRoadmapSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  useEnterStagger(rootRef, "[data-glow-card]", { y: 26, stagger: 0.1 });
  useBandRowAlign(rootRef);

  // md:pt-px — the pink Ownership band above ends exactly on a row line; with
  // (effectively) no top padding this wash starts on that same line: pink →
  // blue directly, no white row between. The 1px blocks the head's aligner
  // margin from collapsing out and pushing the whole section down.
  return (
    <SectionShell id="ref-domain-roadmap" band="blue" className="md:pt-px">
      <SectionHead
        center
        eyebrow="Beyond links"
        gridFill={WASH_FILL}
        title={
          <>
            Your domain does <GradText>more than links.</GradText>
          </>
        }
        lead="Connect the domain once — email forwarding already works today, and there's more on the way."
      />

      <div data-grid-block data-grid-fill={WASH_FILL}>
        <div ref={rootRef} className="grid gap-4 md:grid-cols-2">
          <GlowCard>
            <div className="flex items-center gap-2.5">
              <h3 className="text-[17px] font-bold tracking-[-0.01em]">Email forwarding</h3>
              <WipTag label="Live" variant="green" />
            </div>
            <div className="mt-3 rounded-[9px] bg-[#0d0d12] px-3.5 py-2.5 font-jetbrains text-[12.5px] text-white/85 [overflow-wrap:anywhere]">
              hello@yourbrand.com <span className="text-[#7a7a88]">→</span>{" "}
              <span className="text-[#84d68c]">your inbox</span>
            </div>
            <p className="mt-3 text-[14px] leading-[1.6] text-muted">
              Receive mail on your link domain without running a mail server — forwards land in
              the inbox you already use.
            </p>
          </GlowCard>
          <GlowCard>
            <div className="flex items-center gap-2.5">
              <h3 className="text-[17px] font-bold tracking-[-0.01em]">Nameserver delegation</h3>
              <WipTag label={roadmapItem("nameservers").promised} variant="blue" />
            </div>
            <div className="mt-3 rounded-[9px] bg-[#0d0d12] px-3.5 py-2.5 font-jetbrains text-[12.5px] text-white/85 [overflow-wrap:anywhere]">
              NS <span className="text-[#7a7a88]">→</span>{" "}
              <span className="text-[#84d68c]">ns1.rii.link · ns2.rii.link</span>
            </div>
            <p className="mt-3 text-[14px] leading-[1.6] text-muted">
              Hand us the zone and skip DNS bookkeeping entirely — records, SSL and routing
              managed as one.
            </p>
          </GlowCard>
        </div>
      </div>
    </SectionShell>
  );
}
